import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { boards, cards as cardsApi, templates, github, runner, type Card } from '../lib/api';
import { useAuth } from '../lib/auth';
import { KanbanBoard } from '../components/KanbanBoard';
import { CardDetail } from '../components/CardDetail';
import { CreateCardModal } from '../components/CreateCardModal';
import { useToast } from '../components/Toast';
import { EditBoardModal } from '../components/EditBoardModal';
import { ActivitySidebar } from '../components/ActivitySidebar';
import { ws } from '../lib/websocket';
import { getCreatableColumns } from '../lib/move-rules';

export function BoardView() {
  const { boardId } = useParams<{ boardId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [showCreateCard, setShowCreateCard] = useState(false);
  const [createTargetColumnId, setCreateTargetColumnId] = useState<string | undefined>();
  const [showEditBoard, setShowEditBoard] = useState(false);
  const [mobileActivityOpen, setMobileActivityOpen] = useState(false);
  // Runner status — initialized from board settings, updated via WebSocket runner-status messages
  const [runnerStatus, setRunnerStatus] = useState<'running' | 'stopped' | 'starting' | 'stopping' | 'error'>('stopped');

  // Fetch board
  const { data: boardData, isLoading: boardLoading, error: boardError } = useQuery({
    queryKey: ['board', boardId],
    queryFn: () => boards.get(boardId!),
    enabled: !!boardId && !authLoading && !!user,
  });

  const board = boardData?.data;

  // Fetch cards per-column via GSI2. Each phase is its own partition query, so
  // a big Done column can't push newer cards off the list (the prior bug).
  // Done is fetched desc + small limit so the UI shows the newest completions
  // first; all other phases are asc and sized to cover any realistic count.
  const columnsForQueries = useMemo(() => board?.columns ?? [], [board]);
  const cardQueries = useQueries({
    queries: columnsForQueries.map((col) => {
      const isDone = col.status === 'done';
      return {
        queryKey: ['cards', boardId, col.id] as const,
        queryFn: () =>
          cardsApi.listByColumn(boardId!, col.id, {
            limit: isDone ? 200 : 500,
            direction: isDone ? ('desc' as const) : ('asc' as const),
          }),
        enabled: !!boardId && !authLoading && !!user && !!board,
        refetchInterval: 30_000,
      };
    }),
  });

  const cardsLoading = cardQueries.some((q) => q.isLoading);
  const cardList: Card[] = useMemo(() => {
    const out: Card[] = [];
    for (const q of cardQueries) {
      const data = q.data?.data;
      if (data) out.push(...data);
    }
    return out;
  }, [cardQueries]);

  // Sync initial runner status from board settings (persisted by desktop on status change)
  useEffect(() => {
    const persisted = (board?.settings?.runner as any)?.status;
    if (persisted === 'running' || persisted === 'stopped' || persisted === 'error') {
      setRunnerStatus(persisted);
    }
  }, [board?.settings?.runner]);

  // Derived summary card for selected card (from list cache)
  const summaryCard = selectedCardId ? cardList.find((c) => c.id === selectedCardId) ?? null : null;

  // Fetch full card data lazily when a card is selected
  const { data: fullCardData, isLoading: isLoadingFullCard } = useQuery({
    queryKey: ['card', boardId, selectedCardId],
    queryFn: () => cardsApi.get(boardId!, selectedCardId!),
    enabled: !!boardId && !!selectedCardId,
    staleTime: 30_000,
  });

  // The card passed to CardDetail: full card when loaded, summary card as fallback
  const displayCard: Card | null = fullCardData?.data ?? summaryCard;

  const firstColumnId = useMemo(() => {
    if (!board) return undefined;
    const sorted = [...board.columns].sort((a, b) => a.order - b.order);
    return sorted[0]?.id;
  }, [board]);

  // Open card from URL query param (e.g. ?card=card_123)
  useEffect(() => {
    const cardParam = searchParams.get('card');
    if (cardParam && cardList.length > 0 && !selectedCardId) {
      const found = cardList.find((c) => c.id === cardParam);
      if (found) setSelectedCardId(found.id);
    }
  }, [searchParams, cardList]);

  // Fetch board GitHub settings
  const { data: ghSettingsData } = useQuery({
    queryKey: ['board-github', boardId],
    queryFn: () => github.boardSettings.get(boardId!),
    enabled: !!boardId && !!board,
  });

  const currentRepo = ghSettingsData?.data
    ? { owner: ghSettingsData.data.owner, repo: ghSettingsData.data.repo, tokenId: ghSettingsData.data.tokenId }
    : null;

  // Edit board mutation
  const editBoardMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string; color?: string; githubRepo?: string; githubTokenId?: string; templateId?: string; claudeEnv: Record<string, string> }) => {
      const { githubRepo, githubTokenId, templateId, claudeEnv, color, ...boardFields } = data;

      const nextSettings: any = { ...board?.settings };
      if (color !== undefined) nextSettings.color = color;

      // Always reconcile runner.claudeEnv with the modal's reported state.
      // Empty object means "cleared" — strip the key rather than storing {}.
      const nextRunner = { ...(board?.settings?.runner ?? {}) };
      if (Object.keys(claudeEnv).length > 0) {
        nextRunner.claudeEnv = claudeEnv;
      } else {
        delete nextRunner.claudeEnv;
      }
      nextSettings.runner = nextRunner;

      (boardFields as any).settings = nextSettings;

      await boards.update(boardId!, boardFields as any);
      if (githubRepo) {
        const [owner, repo] = githubRepo.split('/');
        await github.boardSettings.set(boardId!, { owner, repo, tokenId: githubTokenId });
      }
      if (templateId !== undefined) {
        await boards.changeTemplate(boardId!, templateId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] });
      queryClient.invalidateQueries({ queryKey: ['board-github', boardId] });
      queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast('Board updated!', 'success');
      setShowEditBoard(false);
    },
    onError: () => toast('Failed to update board', 'error'),
  });

  // Delete board mutation
  const deleteBoardMutation = useMutation({
    mutationFn: () => boards.delete(boardId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boards'] });
      toast('Board deleted', 'success');
      navigate('/');
    },
    onError: () => toast('Failed to delete board', 'error'),
  });

  // Runner command mutation — sends start/stop to API which relays via WebSocket to desktop
  const runnerMutation = useMutation({
    mutationFn: (action: 'start' | 'stop') => runner.command(boardId!, action),
    onMutate: (action) => {
      setRunnerStatus(action === 'start' ? 'starting' : 'stopping');
    },
    onError: () => {
      // Revert to stopped on error (safest assumption)
      setRunnerStatus('stopped');
      toast('Failed to send runner command', 'error');
    },
  });

  // Fetch the board's template to determine which phases auto-advance
  const templateId = board?.settings?.defaultTemplate;
  const { data: templateData } = useQuery({
    queryKey: ['templates', templateId],
    queryFn: () => templates.get(templateId!),
    enabled: !!templateId,
  });

  const workflow = templateData?.data?.workflow;

  const creatableColumns = useMemo(() => {
    if (!board) return [];
    return getCreatableColumns(board.columns, workflow);
  }, [board, workflow]);

  const creatableColumnIds = useMemo(
    () => new Set(creatableColumns.map((c) => c.id)),
    [creatableColumns]
  );

  // WebSocket for real-time updates
  useEffect(() => {
    if (!boardId) return;
    ws.connect(boardId);
    const unsub = ws.subscribe((msg) => {
      const cardId = (msg as any).cardId as string | undefined;
      if (msg.type === 'card-updated' || msg.type === 'card-moved') {
        queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
        if (cardId) {
          queryClient.invalidateQueries({ queryKey: ['card', boardId, cardId] });
        }
      }
      if (msg.type === 'activity-created') {
        queryClient.invalidateQueries({ queryKey: ['board-activities', boardId] });
        queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
        if (cardId) {
          queryClient.invalidateQueries({ queryKey: ['card-activities', boardId, cardId] });
        }
      }
      if (msg.type === 'board-updated') {
        queryClient.invalidateQueries({ queryKey: ['board', boardId] });
      }
      if (msg.type === 'runner-status') {
        const status = msg.status as 'running' | 'stopped' | 'starting' | 'stopping' | 'error';
        setRunnerStatus(status);
      }
    });
    return () => {
      unsub();
      ws.close();
    };
  }, [boardId, queryClient]);

  // Move card mutation with optimistic update across per-column caches.
  // Removes the card from its current column's cache and inserts it into the
  // target column's cache so the UI updates instantly without waiting for the
  // server round-trip. Reverts both caches on failure.
  const moveMutation = useMutation({
    mutationFn: ({ cardId, columnId, order }: { cardId: string; columnId: string; order?: number }) =>
      cardsApi.move(boardId!, cardId, { columnId, order }),
    onMutate: async ({ cardId, columnId, order }) => {
      await queryClient.cancelQueries({ queryKey: ['cards', boardId] });

      // Find the card and its source column from the merged list
      const moving = cardList.find((c) => c.id === cardId);
      if (!moving) return { snapshots: [] as Array<[readonly unknown[], unknown]> };
      const fromColumnId = moving.columnId;

      const snapshots: Array<[readonly unknown[], unknown]> = [];

      // Snapshot + remove from source column
      const fromKey = ['cards', boardId, fromColumnId] as const;
      snapshots.push([fromKey, queryClient.getQueryData(fromKey)]);
      queryClient.setQueryData<{ data: Card[] }>(fromKey, (old) => {
        if (!old) return old;
        return { ...old, data: old.data.filter((c) => c.id !== cardId) };
      });

      // Snapshot + insert into target column
      const toKey = ['cards', boardId, columnId] as const;
      snapshots.push([toKey, queryClient.getQueryData(toKey)]);
      const isColumnChange = fromColumnId !== columnId;
      const updatedCard: Card = {
        ...moving,
        columnId,
        order: order ?? moving.order,
        metadata:
          isColumnChange && moving.metadata.claim
            ? { ...moving.metadata, claim: undefined }
            : moving.metadata,
      };
      queryClient.setQueryData<{ data: Card[] }>(toKey, (old) => {
        const existing = old?.data ?? [];
        const without = existing.filter((c) => c.id !== cardId);
        return { ...(old ?? {}), data: [...without, updatedCard] };
      });

      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      for (const [key, value] of context?.snapshots ?? []) {
        queryClient.setQueryData(key, value);
      }
      toast('Failed to move card', 'error');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
    },
  });

  // Create card — async handler used by CreateCardModal
  const handleCreateCard = async (data: { title?: string; description: string; columnId?: string }): Promise<{ id: string }> => {
    const result = await cardsApi.create(boardId!, data);
    if (!result.success || !result.data) throw new Error('Failed to create card');
    queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
    toast('Card created!', 'success');
    setShowCreateCard(false);
    setCreateTargetColumnId(undefined);
    return { id: result.data.id };
  };

  if (boardLoading || cardsLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  if (boardError || !board) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <p className="text-sm text-red-400">Board not found</p>
        <button
          onClick={() => navigate('/command')}
          className="rounded-lg bg-gray-800 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
        >
          Back to Command Center
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Board header */}
      <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3 md:px-6 md:py-4">
        <div className="flex min-w-0 items-center gap-2 md:gap-3">
          <button onClick={() => navigate('/')} className="shrink-0 text-gray-500 hover:text-gray-300">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </button>
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold md:text-lg">{board.name}</h1>
            {board.description && <p className="hidden text-xs text-gray-500 md:block">{board.description}</p>}
          </div>
          <button
            onClick={() => setShowEditBoard(true)}
            className="shrink-0 rounded-lg p-1.5 text-gray-500 hover:bg-gray-800 hover:text-gray-300"
            title="Board settings"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
        <div className="flex items-center gap-2">
          {/* Runner start/stop control */}
          <div className="hidden items-center gap-1.5 md:flex">
            <span className={`text-xs font-medium ${
              runnerStatus === 'running' ? 'text-green-400' :
              runnerStatus === 'starting' || runnerStatus === 'stopping' ? 'text-yellow-400' :
              runnerStatus === 'error' ? 'text-red-400' :
              'text-gray-500'
            }`}>
              Runner: {runnerStatus}
            </span>
            {runnerStatus === 'running' || runnerStatus === 'stopping' ? (
              <button
                onClick={() => runnerMutation.mutate('stop')}
                disabled={runnerStatus === 'stopping' || runnerMutation.isPending}
                className="rounded-md bg-gray-800 px-2.5 py-1 text-xs font-medium text-gray-300 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Stop
              </button>
            ) : (
              <button
                onClick={() => runnerMutation.mutate('start')}
                disabled={runnerStatus === 'starting' || runnerMutation.isPending}
                className="rounded-md bg-gray-800 px-2.5 py-1 text-xs font-medium text-gray-300 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Start Runner
              </button>
            )}
          </div>
          {/* Mobile activity button */}
          <button
            onClick={() => setMobileActivityOpen(true)}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-800 hover:text-gray-200 md:hidden"
            title="Activity"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
          {/* Desktop add card */}
          <button
            onClick={() => { setCreateTargetColumnId(undefined); setShowCreateCard(true); }}
            className="hidden items-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-500 md:flex"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Card
          </button>
        </div>
      </div>

      {/* Kanban board */}
      <div className="flex-1 overflow-hidden">
        <KanbanBoard
          board={board}
          cards={cardList}
          workflow={workflow}
          onMoveCard={(cardId, columnId, order) => moveMutation.mutate({ cardId, columnId, order })}
          onCardClick={(card) => {
            setSelectedCardId(card.id);
            setSearchParams((prev) => { prev.set('card', card.id); return prev; }, { replace: true });
          }}
          onBlockedDrop={() => toast('This phase is handled by an agent — cards advance here automatically', 'info')}
          creatableColumnIds={creatableColumnIds}
          onCreatableColumnEmptyClick={(columnId) => { setCreateTargetColumnId(columnId); setShowCreateCard(true); }}
        />
      </div>

      {/* Mobile FAB for adding cards */}
      <button
        onClick={() => { setCreateTargetColumnId(undefined); setShowCreateCard(true); }}
        className="fixed bottom-6 right-6 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg shadow-primary-900/40 active:bg-primary-500 md:hidden"
        title="Add Card"
      >
        <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      </button>

      {/* Mobile activity overlay */}
      {mobileActivityOpen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-surface-900 md:hidden">
          <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3">
            <span className="text-sm font-semibold">Activity</span>
            <button onClick={() => setMobileActivityOpen(false)} className="rounded-md p-1.5 text-gray-400 hover:text-gray-200">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <ActivitySidebar isOpen onToggle={() => setMobileActivityOpen(false)} isMobileOverlay />
          </div>
        </div>
      )}

      {/* Card detail panel */}
      {selectedCardId && displayCard && (
        <CardDetail
          card={displayCard}
          boardId={boardId!}
          board={board}
          workflow={workflow}
          allCards={cardList}
          isLoadingFull={isLoadingFullCard}
          onMoveCard={(cardId, columnId) => moveMutation.mutate({ cardId, columnId })}
          onClose={() => {
            setSelectedCardId(null);
            setSearchParams((prev) => {
              prev.delete('card');
              return prev;
            });
          }}
        />
      )}

      {/* Create card modal */}
      {showCreateCard && (
        <CreateCardModal
          onSubmit={handleCreateCard}
          onClose={() => { setShowCreateCard(false); setCreateTargetColumnId(undefined); }}
          boardId={boardId!}
          defaultColumnId={createTargetColumnId}
          creatableColumns={creatableColumns}
        />
      )}

      {/* Edit board modal */}
      {showEditBoard && (
        <EditBoardModal
          board={board}
          currentRepo={currentRepo}
          onSave={(data) => editBoardMutation.mutate(data)}
          onDelete={() => deleteBoardMutation.mutate()}
          onClose={() => setShowEditBoard(false)}
        />
      )}
    </div>
  );
}
